import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useProfileStore, getFacilityKeyForPortal, getPatientKey } from "@/store/profile";
import { useDemographicsStore } from "@/store/demographics";
import authRequest from "@/api/auth-request";
import type { PatientCommOptions } from "@/api/auth-request";
import { parseApiBoolResult } from "@/utils/parseApiBoolResult";
import { toast } from "sonner";

const formatPhoneDisplay = (v: string) => {
  const d = String(v).replace(/\D/g, "").slice(0, 10);
  if (d.length >= 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length >= 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length >= 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return d;
};

function formatPhoneForDisplay(v: string): string {
  if (!v) return "";
  return formatPhoneDisplay(v);
}

const defaultComm: PatientCommOptions = {
  patientID: 0,
  mobilePhone: "",
  homePhone: "",
  email: "",
  sendSms: false,
  sendEmail: false,
  callMobilePhone: false,
  callHomePhone: false,
  otherEmail: false,
  twoWaySms: false,
};

export interface CommInfoHandle {
  /** Trigger save and return true on success. */
  save: () => Promise<boolean>;
}

export interface CommInfoProps {
  /** Called after successful save. When provided, the component is in wizard mode. */
  onSaved?: () => void;
  /** When false, hides the standalone UPDATE button (wizard provides its own actions). */
  showSubmitButton?: boolean;
}

export const CommInfo = forwardRef<CommInfoHandle, CommInfoProps>(function CommInfo(
  { onSaved, showSubmitButton = true },
  ref
) {
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const demographics = useDemographicsStore((s) => s.demographics);
  const [commOptions, setCommOptions] = useState<PatientCommOptions>({ ...defaultComm });
  const [mobilenum, setMobilenum] = useState("");

  const facilityKey = getFacilityKeyForPortal();
  const patientKey = getPatientKey();
  const officeName = currentPatient?.officeName ?? "";

  const fetchDemographics = useDemographicsStore((s) => s.fetchDemographics);

  useEffect(() => {
    if (!facilityKey || !patientKey) return;
    fetchDemographics().catch(() => {
      /* ignore — hasHomePhone / hasMobilePhone still use comm API fields */
    });
  }, [facilityKey, patientKey, fetchDemographics]);

  const demMobileDigits = (demographics?.mobilePhone ?? "").replace(/\D/g, "");
  const demHomeDigits = (demographics?.homePhone ?? "").replace(/\D/g, "");

  const mobileDigits = (mobilenum || commOptions.mobilePhone || "").replace(/\D/g, "");
  const hasMobilePhone =
    mobileDigits.length >= 10 || demMobileDigits.length >= 10;

  const commHomeDigits = (commOptions.homePhone || "").replace(/\D/g, "");
  const hasHomePhone =
    commHomeDigits.length >= 10 || demHomeDigits.length >= 10;

  // A safe, always-a-string mobile number for display. Empty (never "0" or a
  // partial) when there is no valid mobile on file, so it can't leak into the UI.
  const mobileNumberDisplay = hasMobilePhone
    ? mobilenum || formatPhoneForDisplay(String(commOptions.mobilePhone ?? ""))
    : "";

  useEffect(() => {
    if (!hasMobilePhone && commOptions.twoWaySms) {
      setCommOptions((prev) => ({ ...prev, twoWaySms: false }));
    }
  }, [hasMobilePhone, commOptions.twoWaySms]);

  useEffect(() => {
    if (!hasMobilePhone && commOptions.callMobilePhone) {
      setCommOptions((prev) => ({ ...prev, callMobilePhone: false }));
    }
  }, [hasMobilePhone, commOptions.callMobilePhone]);

  useEffect(() => {
    if (!hasHomePhone && commOptions.callHomePhone) {
      setCommOptions((prev) => ({ ...prev, callHomePhone: false }));
    }
  }, [hasHomePhone, commOptions.callHomePhone]);

  useEffect(() => {
    if (!hasMobilePhone && commOptions.sendSms) {
      setCommOptions((prev) => ({ ...prev, sendSms: false }));
    }
  }, [hasMobilePhone, commOptions.sendSms]);

  useEffect(() => {
    if (!facilityKey || !patientKey) return;
    authRequest.request
      .getPatientCommOptions({ facilityKey, patientKey })
      .then((res) => {
        const data = res.data ?? defaultComm;
        // Default email and text message ON for appointment reminders (new users)
        setCommOptions({
          ...data,
          sendEmail: data.sendEmail !== false,
          sendSms: data.sendSms !== false,
        });
        setMobilenum(data.mobilePhone ? formatPhoneDisplay(data.mobilePhone) : "");
      })
      .catch(console.error);
  }, [facilityKey, patientKey]);

  const update = (patch: Partial<PatientCommOptions>) =>
    setCommOptions((prev) => ({ ...prev, ...patch }));

  const saveCommPrefs = async () => {
    const atLeastOneReminder =
      commOptions.sendEmail ||
      commOptions.sendSms ||
      commOptions.callMobilePhone ||
      commOptions.callHomePhone;
    if (!atLeastOneReminder) {
      toast.error("Please choose at least one option for appointment reminders (Email, Text, Call Home, or Call Mobile).");
      return false;
    }
    const payload = {
      facilityKey,
      patientKey,
      sendSms: commOptions.sendSms ? 1 : 0,
      sendEmail: commOptions.sendEmail ? 1 : 0,
      callMobilePhone: commOptions.callMobilePhone ? 1 : 0,
      callHomePhone: commOptions.callHomePhone ? 1 : 0,
      twoWaySms: hasMobilePhone && !!commOptions.twoWaySms,
      otherEmail: !!commOptions.otherEmail,
      mobilePhone: mobilenum.replace(/\D/g, ""),
      homePhone: commOptions.homePhone ?? "",
      email: commOptions.email ?? "",
    };
    try {
      const result = await authRequest.request.updatePatientCommOptions(payload);
      const { success, reason } = parseApiBoolResult(result.data);
      if (success) {
        toast.success("Communication preferences updated");
        return true;
      }
      toast.error((reason && reason.trim()) || "Communication preferences update failed");
      return false;
    } catch {
      toast.error("Communication preferences update failed");
      return false;
    }
  };

  useImperativeHandle(ref, () => ({ save: saveCommPrefs }), [saveCommPrefs]);

  const updatePatientCommOption = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await saveCommPrefs();
    if (ok && onSaved) onSaved();
  };

  // Below sm the cards are flattened onto the page background so the options run to the page gutter.
  const cardClass =
    "sm:overflow-hidden sm:rounded-xl sm:border sm:border-healthsource-lightLavender sm:bg-white sm:p-5 sm:shadow-[0_2px_8px_rgba(30,37,43,0.06)]";
  // 44px tap rows for the native checkboxes and radios.
  const optionClass = "flex min-h-11 items-center gap-3";
  const inputClass = "h-5 w-5 shrink-0 accent-healthsource-navy";

  return (
    <form className="space-y-6" onSubmit={updatePatientCommOption}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left: Appointment Reminders */}
        <div className={cardClass}>
          <h3 className="mb-2 text-[15px] font-extrabold text-healthsource-dark">
            Appointment Reminders
          </h3>
          <div>
            <label className={optionClass}>
              <input
                type="checkbox"
                checked={commOptions.sendEmail}
                onChange={(e) => update({ sendEmail: e.target.checked })}
                className={inputClass}
              />
              <span className="min-w-0 break-words text-[15px] font-bold text-healthsource-dark">
                Send via Email
                {commOptions.sendEmail && (commOptions.email ?? "").trim() && (
                  <span className="font-normal text-healthsource-gray"> ({commOptions.email})</span>
                )}
              </span>
            </label>
            <label className={optionClass}>
              <input
                type="checkbox"
                checked={commOptions.sendSms}
                disabled={!hasMobilePhone || commOptions.callMobilePhone || commOptions.callHomePhone}
                onChange={(e) => update({ sendSms: e.target.checked })}
                className={inputClass}
              />
              <span className="min-w-0 break-words text-[15px] font-bold text-healthsource-dark">
                Send via Text Message
                {commOptions.sendSms && mobileNumberDisplay && (
                  <span className="font-normal text-healthsource-gray"> ({mobileNumberDisplay})</span>
                )}
              </span>
            </label>
            <label className={optionClass}>
              <input
                type="checkbox"
                checked={commOptions.callHomePhone}
                disabled={!hasHomePhone || commOptions.sendSms || commOptions.callMobilePhone}
                onChange={(e) => update({ callHomePhone: e.target.checked })}
                className={inputClass}
              />
              <span className="min-w-0 break-words text-[15px] font-bold text-healthsource-dark">
                Call Home
                {commOptions.callHomePhone && (commOptions.homePhone ?? "").trim() && (
                  <span className="font-normal text-healthsource-gray"> ({formatPhoneForDisplay(commOptions.homePhone)})</span>
                )}
              </span>
            </label>
            <label className={optionClass}>
              <input
                type="checkbox"
                checked={commOptions.callMobilePhone}
                disabled={!hasMobilePhone || commOptions.sendSms || commOptions.callHomePhone}
                onChange={(e) => update({ callMobilePhone: e.target.checked })}
                className={inputClass}
              />
              <span className="min-w-0 break-words text-[15px] font-bold text-healthsource-dark">
                Call Mobile
                {commOptions.callMobilePhone && mobileNumberDisplay && (
                  <span className="font-normal text-healthsource-gray"> ({mobileNumberDisplay})</span>
                )}
              </span>
            </label>
          </div>
        </div>

        {/* Right: Text and Email stacked */}
        <div className="flex flex-col gap-6">
          <div className={cardClass}>
            <h3 className="mb-2 text-[15px] font-extrabold text-healthsource-dark">
              Text Message Communication
            </h3>
            <p className="mb-3 text-[13px] font-semibold leading-snug text-healthsource-gray">
              I authorize {officeName || "[Facility Name]"} to communicate with me via SMS messages, including appointment reminders, messages from your care team, and health-related updates. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe at any time. Reply HELP for assistance. Consent is not a condition of receiving care. View our{" "}
              <span className="cursor-default text-healthsource-navy underline decoration-healthsource-navy/30 underline-offset-2 transition-colors hover:text-healthsource-red hover:decoration-healthsource-red/40" title="Coming soon" aria-label="Privacy policy (coming soon)">Privacy Policy</span>
              {" "}and{" "}
              <span className="cursor-default text-healthsource-navy underline decoration-healthsource-navy/30 underline-offset-2 transition-colors hover:text-healthsource-red hover:decoration-healthsource-red/40" title="Coming soon" aria-label="Terms of use (coming soon)">Terms &amp; Conditions</span>.
            </p>
            <div className="flex flex-wrap items-center gap-x-6">
              <label className={`${optionClass} ${!hasMobilePhone ? "cursor-not-allowed opacity-70" : ""}`}>
                <input
                  type="radio"
                  name="questionSMS"
                  checked={!!commOptions.twoWaySms}
                  disabled={!hasMobilePhone}
                  onChange={() => update({ twoWaySms: true })}
                  className={inputClass}
                />
                <span className="text-[15px] font-bold text-healthsource-dark">Yes</span>
              </label>
              <label className={optionClass}>
                <input
                  type="radio"
                  name="questionSMS"
                  checked={!commOptions.twoWaySms}
                  onChange={() => update({ twoWaySms: false })}
                  className={inputClass}
                />
                <span className="text-[15px] font-bold text-healthsource-dark">No</span>
              </label>
              {!hasMobilePhone ? (
                <span className="text-[13px] font-semibold italic text-healthsource-gray">
                  (No Mobile Phone Entered)
                </span>
              ) : commOptions.twoWaySms && mobileNumberDisplay ? (
                <span className="break-words text-[15px] font-bold text-healthsource-dark">
                  {mobileNumberDisplay}
                </span>
              ) : null}
            </div>
          </div>

          <div className={cardClass}>
            <h3 className="mb-2 text-[15px] font-extrabold text-healthsource-dark">
              Email
            </h3>
            <p className="mb-3 text-[13px] font-semibold text-healthsource-gray">
              Please send me occasional emails about my health and what&apos;s occurring at {officeName}
            </p>
            <div className="flex flex-wrap items-center gap-x-6">
              <label className={optionClass}>
                <input
                  type="radio"
                  name="questionEmail"
                  checked={!!commOptions.otherEmail}
                  onChange={() => update({ otherEmail: true })}
                  className={inputClass}
                />
                <span className="text-[15px] font-bold text-healthsource-dark">Yes</span>
              </label>
              <label className={optionClass}>
                <input
                  type="radio"
                  name="questionEmail"
                  checked={!commOptions.otherEmail}
                  onChange={() => update({ otherEmail: false })}
                  className={inputClass}
                />
                <span className="text-[15px] font-bold text-healthsource-dark">No</span>
              </label>
              {commOptions.otherEmail && (commOptions.email ?? "").trim() && (
                <span className="break-words text-[15px] font-bold text-healthsource-dark">
                  {commOptions.email}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSubmitButton && (
        <div>
          <button
            type="submit"
            className="min-h-11 w-full rounded-md px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90 sm:w-auto"
            style={{ backgroundColor: "var(--hs-red)" }}
          >
            UPDATE
          </button>
        </div>
      )}
    </form>
  );
});
